import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { AppID, Environment } from '@playq/octopus-common';
import { AttachmentSearchQuery, AttachmentsResponse } from '@playq/services-beetle';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { IUseQueryOptionsExtended, useEitherQuery, useQueryPrefetch } from '/api/service-hooks';
import { services2 } from '/api/services2';

import { calculateTotalAttachment } from '../helpers';

export const searchGlobalAttachmentQueryKeys: unknown[] = ['beetle', 'support', 'search', 'attachments', 'global'];

export const useSearchAttachments = (
  appId: AppID,
  query: AttachmentSearchQuery,
  env: Environment,
  iterator: OffsetLimit,
  options?: IUseQueryOptionsExtended<AttachmentsResponse | undefined, GenericFailure | Error>
) => {
  const queryClient = useQueryClient();
  const keys = useMemo(() => searchGlobalAttachmentQueryKeys.concat(env, query, iterator), [env, query, iterator]);
  const { mutate: _mutate, ...res } = useEitherQuery(
    keys,
    () => services2.audienceSupportService.searchAttachments(appId, query, iterator, env),
    {
      placeholderData: keepPreviousData,
      ...options,
    }
  );
  const { nextIterator, preFetchData, nextKeys, setPreFetchData } = useQueryPrefetch({
    keys,
    enabled: true,
    total: res.data?.attachments.length,
    args: [appId, query, iterator, env],
    serviceMethod: services2.audienceSupportService.searchAttachments.bind(services2.audienceSupportService),
    hasMore: res.data?.attachments.length === iterator.limit ? true : undefined,
  });
  useEffect(() => {
    if (nextKeys && res.data?.attachments.length === iterator.limit) {
      setPreFetchData(queryClient.getQueryData(nextKeys));
    }
    return () => setPreFetchData(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iterator.limit, queryClient, res.data?.attachments.length, setPreFetchData]);
  const attachments = res.data?.attachments || [];

  const total = calculateTotalAttachment(
    nextIterator,
    attachments.length,
    iterator,
    preFetchData?.attachments.length ?? iterator.limit
  );

  return {
    attachments,
    total,
    ...res,
  };
};
